import { useCallback } from 'react'
import { trackGoal } from '@lunary/ui-web/analytics'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DailyPickTodayResponseDto, OrbitResponseDto } from '@lunary/api-client'
import { api } from '../../shared/api/client'
import { useRefreshLunars } from '../lunars/queries'

export const PICK_TODAY_KEY = ['daily-pick', 'today'] as const
export const ORBIT_KEY = ['orbit'] as const

export function usePickToday(userId: string | null) {
  return useQuery({
    queryKey: [...PICK_TODAY_KEY, userId],
    queryFn: () => api.get<DailyPickTodayResponseDto>('/daily-picks/today'),
    enabled: userId !== null,
    retry: false,
  })
}

export function useMakePick() {
  const qc = useQueryClient()
  const refreshLunars = useRefreshLunars()
  return useMutation({
    mutationFn: (position: number) => api.post<DailyPickTodayResponseDto>('/daily-picks/today', { position }),
    onSuccess: () => {
      trackGoal('pick_done')
      void qc.invalidateQueries({ queryKey: ORBIT_KEY })
      refreshLunars()
    },
  })
}

export function useOpenExtraPick() {
  const qc = useQueryClient()
  const refreshLunars = useRefreshLunars()
  return useMutation({
    mutationFn: () => api.post<DailyPickTodayResponseDto>('/daily-picks/today/extra'),
    onSuccess: (data) => {
      trackGoal('pick_extra')
      qc.setQueriesData<DailyPickTodayResponseDto>({ queryKey: PICK_TODAY_KEY }, data)
      refreshLunars()
    },
  })
}

export function useChoosePick() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (position: number) => api.post<DailyPickTodayResponseDto>('/daily-picks/today/choose', { position }),
    onSuccess: (data) => {
      trackGoal('pick_choose')
      qc.setQueriesData<DailyPickTodayResponseDto>({ queryKey: PICK_TODAY_KEY }, data)
      void qc.invalidateQueries({ queryKey: ORBIT_KEY })
    },
  })
}

export function useShowPick() {
  const qc = useQueryClient()
  return useCallback((data: DailyPickTodayResponseDto) => {
    qc.setQueriesData<DailyPickTodayResponseDto>({ queryKey: PICK_TODAY_KEY }, data)
  }, [qc])
}

export function useOrbit(userId: string | null) {
  return useQuery({
    queryKey: [...ORBIT_KEY, userId],
    queryFn: () => api.get<OrbitResponseDto>('/users/me/orbit'),
    enabled: userId !== null,
    retry: false,
  })
}
